/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ProductTypeEN,
    RequestCategory,
    Contract,
    hasUnpaidAmmount,
    isVDLContract,
    isFinancialLease,
    isLounLlDSocial,
} from 'cpfr-common';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import { Combobox, ErrorMessage, FormField, FormFieldLabel, Heading } from '@vwfs-bronson/bronson-react';
import { FormikProps, useField, useFormikContext } from 'formik';
import { head, kebabCase, uniqBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestInitialValues } from '../initialValues';
import { ComboboxOption } from '../../../../types/bronson-react';
import { categoriesPerProduct } from './consts';
import { useIbanStatusTag } from '../../../contracts/contract-details/financial-details/iban-section/consult-view/useIbanStatusTag';

type CategorySelectionProps = {
    contractsSelection: Contract[];
    currentCategorySelection: string;
    // eslint-disable-next-line
    resetForm: Function;
};

export const CategorySelection: React.FC<CategorySelectionProps> = ({
    contractsSelection,
    currentCategorySelection,
    resetForm,
}) => {
    const { t } = useTranslation('request');
    const [, meta, helpers] = useField('categorySelection');
    const { initialValues }: FormikProps<RequestInitialValues> = useFormikContext();
    const isIbanStatusTag = useIbanStatusTag();

    const defaultState = currentCategorySelection
        ? {
              value: [
                  {
                      value: t(`category-selection.options.${kebabCase(currentCategorySelection)}`),
                      key: currentCategorySelection,
                  },
              ],
          }
        : {
              value: [
                  {
                      value: t('category-selection.placeholder'),
                      key: 'placeholder',
                  },
              ],
          };

    const [selectedItem, setSelectedItem] = useState<{ value: ComboboxOption[] }>(defaultState);
    const mandatoryFieldsInfo = t('category-selection.mandatory-fields-info');

    useEffect(() => {
        setSelectedItem(
            currentCategorySelection
                ? {
                      value: [
                          {
                              value: t(`category-selection.options.${kebabCase(currentCategorySelection)}`),
                              key: currentCategorySelection,
                          },
                      ],
                  }
                : {
                      value: [
                          {
                              value: t('category-selection.placeholder'),
                              key: 'placeholder',
                          },
                      ],
                  },
        );
    }, [currentCategorySelection]); // eslint-disable-line react-hooks/exhaustive-deps

    const getCategoriesForProduct = (): RequestCategory[] => {
        const contractEndDateWas6MonthAgo = contractsSelection.find((item) =>
            formatCpDate(item.contractEndDate).add(6, 'month').toMoment().isBefore(new Date()),
        );
        if (!contractsSelection.length || contractEndDateWas6MonthAgo) {
            return ['OTHER'];
        }

        const selectedProductTypesEN: Array<string | undefined> = uniqBy(contractsSelection, 'productTypeEN').map(
            (item) => item.productTypeEN,
        );
        const excludedProductTypesEN = ['CHANGE_DURATION_MILEAGE', 'VEHICLE_CLAIM'];

        // check if CHANGE_DURATION_MILEAGE category should be disabled
        const isChangeDurationMileageCategoryDisabled = (): boolean => {
            if (!selectedProductTypesEN.includes(ProductTypeEN.FINANCIAL_LEASE)) return false;

            for (const selectedContract of contractsSelection) {
                const productType = selectedContract.productType;
                const isCreditBailType = isFinancialLease(productType);
                return isCreditBailType && !['CBPN_FL', 'CBPO_FL'].includes(productType || '');
            }

            return false;
        };

        let list: RequestCategory[] = [];

        if (selectedProductTypesEN.length === 1) {
            const selectedTypeEN = head(selectedProductTypesEN);

            // if contracts have similar types
            if (contractsSelection.length > 1) {
                list = selectedTypeEN
                    ? categoriesPerProduct[selectedTypeEN].filter((item) => !excludedProductTypesEN.includes(item))
                    : ['OTHER'];
            } else {
                //can be removed
                if (isVDLContract(contractsSelection[0])) {
                    list = ['DOCUMENTS', 'OTHER'];
                } else {
                    list = selectedTypeEN ? categoriesPerProduct[selectedTypeEN] : ['OTHER'];
                }
            }

            if (isLounLlDSocial(contractsSelection[0]) && !list.includes('DEADLINE')) {
                // eslint-disable-next-line
                list[0] === 'VEHICLE_CLAIM' ? list.splice(1, 0, 'DEADLINE') : list.unshift('DEADLINE');
            }
        } else {
            const allowedCategories: RequestCategory[] = [];

            for (const productType of Object.keys(categoriesPerProduct)) {
                // skip when productType is not matching any selected contract product type
                if (!selectedProductTypesEN.find((item) => item === productType)) continue;

                for (const category of categoriesPerProduct[productType]) {
                    // skip when category is already added to dropdown
                    if (allowedCategories.includes(category)) continue;
                    let addItem = true;

                    // check if category is allowed for all selected contract types
                    for (const selectedProductType of selectedProductTypesEN) {
                        if (selectedProductType && !categoriesPerProduct[selectedProductType].includes(category)) {
                            addItem = false;
                        }
                    }

                    if (addItem) {
                        allowedCategories.push(category);
                    }
                }
            }

            list = allowedCategories.filter((item) => !excludedProductTypesEN.includes(item));
        }

        // remove CHANGE_DURATION_MILEAGE category if disabled because of credit bail
        if (isChangeDurationMileageCategoryDisabled()) {
            list = list.filter((item) => item !== 'CHANGE_DURATION_MILEAGE');
        }

        const isAnyContractLeasingSocial = contractsSelection.some((contract) => contract.isLeasingSocial);
        if (isAnyContractLeasingSocial) {
            const socialLeasingRestrictedActions = ['TENANT'];
            list = list.filter((item) => !socialLeasingRestrictedActions.includes(item));
        }

        // If a person has unpaid amount, we should block the following functionalities.
        // https://confluence.platform.vwfs.io/display/CPFR/Display+of+unpaid+details+and+payment
        if (contractsSelection.find((item) => hasUnpaidAmmount(item))) {
            const restrictedActions = ['CHANGE_DURATION_MILEAGE', 'TENANT', 'REQUEST_CONTRACT_TRANSFER', 'DEADLINE'];
            list = list.filter((item) => !restrictedActions.includes(item));
        }

        if (isIbanStatusTag) {
            list = list.filter((item) => item !== 'AUTHORIZATION_DEBIT');
        }

        return list;
    };

    const renderOptions = (): ComboboxOption[] => {
        const categories = getCategoriesForProduct();
        if (categories && Array.isArray(categories))
            return categories.map((category: RequestCategory) => ({
                key: category,
                value: t(`category-selection.options.${kebabCase(category)}`),
            }));
        return [];
    };

    const setValueChange = useCallback(
        (selectedCategory: any) => {
            setSelectedItem({ value: [selectedCategory[0]] });
            if (selectedCategory[0]?.key !== currentCategorySelection && !contractsSelection.length) {
                return resetForm({
                    values: { ...initialValues, contractsSelection },
                });
            }
            return helpers.setValue(selectedCategory[0]?.key || initialValues.categorySelection);
        },
        [contractsSelection, currentCategorySelection], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const labelText = `${t('category-selection.label')}`;

    return (
        <>
            <p>{mandatoryFieldsInfo}</p>
            <FormFieldLabel htmlFor="category-selection">
                <Heading level={3}>{labelText}</Heading>
            </FormFieldLabel>
            <FormField
                type="select"
                testId="category-selection"
                id={'category-selection'}
                errorMessage={
                    meta.touched &&
                    meta.error && <ErrorMessage testId="textarea-error-message">{meta.error}</ErrorMessage>
                }
            >
                <Combobox
                    testId="custom-contract-select"
                    defaultState={defaultState}
                    onChange={setValueChange}
                    value={selectedItem.value}
                    options={renderOptions()}
                ></Combobox>
            </FormField>
        </>
    );
};
